import PageHero from "@/components/PageHero";
import CTABanner from "@/components/CTABanner";
import { useSEO } from "@/lib/seo";
import { ABOUT_HERO, TEAM } from "@/lib/content";

export default function About() {
  useSEO({
    title: "About, Bellhouse Law",
    description:
      "Bellhouse Law is a boutique Sunshine Coast firm built around clarity, responsiveness and commercial outcomes.",
  });

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About"
        title="A boutique firm, intentionally small."
        subtitle="Founded to give clients the calibre of legal thinking usually reserved for top-tier firms, delivered with the warmth and accessibility of a boutique practice."
        image={ABOUT_HERO}
        testId="about-hero"
      />

      {/* Story */}
      <section className="section-pad bg-bone">
        <div className="container-narrow grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-20">
          <div className="md:col-span-5">
            <span className="eyebrow">Our story</span>
            <h2 className="mt-4 text-4xl font-bold leading-tight text-ink md:text-5xl lg:text-[56px]">
              Built around clients, not billable units.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6 text-base leading-relaxed text-ink-soft md:text-lg">
            <p>
              Bellhouse Law was founded with a single belief, legal advice
              should make decisions easier, not harder. Too often we saw
              clients leave law firms with a memo full of caveats and no clear
              path forward. We do the opposite, we listen first, distil the
              risk, and give you a recommendation you can actually act on.
            </p>
            <p>
              Today our practice serves home buyers and sellers, growing
              businesses, property investors and developers across Queensland.
              The relationships we build tend to last decades, spanning
              multiple transactions, generations and life stages.
            </p>
          </div>
        </div>
      </section>

      {/* Managing Team */}
      <section
        className="section-pad bg-sage-50/60"
        data-testid="managing-team-section"
      >
        <div className="container-narrow">
          <div className="mb-14 max-w-2xl">
            <span className="eyebrow">Our Managing Team</span>
            <h2 className="mt-4 text-4xl font-bold leading-tight text-ink md:text-5xl lg:text-[56px]">
              The people behind the firm.
            </h2>
          </div>

          <div className="space-y-24">
            {TEAM.map((person, i) => (
              <div
                key={person.name}
                className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-16 ${
                  i % 2 === 1 ? "md:[direction:rtl]" : ""
                }`}
                data-testid={`team-${person.name.split(" ")[0].toLowerCase()}`}
              >
                <div className="md:col-span-5 md:[direction:ltr]">
                  <div className="aspect-[4/5] overflow-hidden bg-sage-100">
                    <img
                      src={person.image}
                      alt={person.name}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
                <div className="md:col-span-7 md:[direction:ltr]">
                  <div className="text-3xl font-bold text-ink md:text-4xl">
                    {person.name}
                  </div>
                  <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.22em] text-sage-700">
                    {person.role}
                  </div>
                  <div className="mt-7 space-y-5 text-base leading-relaxed text-ink-soft md:text-lg">
                    {person.bio.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Combined experience banner */}
      <section
        className="bg-ink py-24 md:py-32"
        data-testid="combined-experience-banner"
      >
        <div className="container-narrow grid grid-cols-1 items-center gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="text-[11px] font-medium uppercase tracking-[0.32em] text-sage-200">
              Team Experience
            </div>
            <h3 className="mt-5 text-5xl font-bold leading-[0.95] text-white md:text-6xl lg:text-[80px]">
              Over 30 Years
              <br />
              <span className="italic text-sage-200">
                of Combined Experience
              </span>
            </h3>
          </div>
          <p className="md:col-span-7 md:col-start-7 text-base leading-relaxed text-white/80 md:text-lg">
            Our team brings more than 30 years of combined experience advising
            individuals, businesses, investors and developers across
            Queensland. We combine practical legal expertise with responsive
            service and commercially focused advice to help clients achieve
            confident outcomes.
          </p>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
